import { useMemo, useState, ChangeEvent, useEffect } from 'react';

import { CellProps } from 'react-table';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';

import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { Vertical, Flexbox } from 'src/components/Alignments';
import FilterPanel from 'src/components/FilterPanel';
import AddCPCCasesDialog from 'src/components/Modal/AddCPCCasesDialog';
import AddCaseDialog from 'src/components/Modal/AddCaseDialog';
import { TableWrapper } from 'src/components/SharedTableStyles';
import Spinner from 'src/components/Spinner';
import Table from 'src/components/Table';
import useDeferredState from 'src/hooks/useDeferredState';
import { jobState, useRefreshJobList } from 'src/states/job';
import {
  projectState,
  useRefreshProject,
  useRefreshProjectList,
} from 'src/states/project';
import { Modality } from 'src/types/api/data/image';
import { JobReadSchema, JobState } from 'src/types/api/data/job';
import { AnnotatorSchema } from 'src/types/api/data/project';
import { SelectOption } from 'src/types/client/ui';
import { DEFAULT_PAGE_SIZE } from 'src/utils/constants';
import { getEllipsis } from 'src/utils/string';

import AnnotatorFilter from './AnnotatorFilter';
import JobStateFilter, {
  JobStateOption,
  DEFAULT_JOB_STATE_OPTION,
} from './JobStateFilter';
import JobTypeFilter, {
  DEFAULT_JOB_TYPE_OPTION,
  JobTypeOption,
} from './JobTypeFilter';

interface JobsProps {
  projectId: string;
  modality: Modality;
  annotator?: string;
  annotators?: AnnotatorSchema[];
}

const DEFAULT_ANNOTATOR_OPTION = {
  value: 'all',
  label: 'All users',
};

export default function Jobs({
  projectId,
  modality,
  annotators,
}: JobsProps): JSX.Element {
  const userOption = useRecoilValue(jobState.jobUserOption);
  const project = useRecoilValue(projectState.project({ projectId, modality }));
  const refreshProjectList = useRefreshProjectList();
  const refreshProject = useRefreshProject();
  const refreshJobList = useRefreshJobList();

  const [isDialogVisible, setIsDialogVisible] = useDeferredState(false);
  const [page, setPage] = useState<number>(1);
  const [jobTypeOption, setJobTypeOption] = useState<JobTypeOption>(
    DEFAULT_JOB_TYPE_OPTION
  );
  const [stateOption, setStateOption] = useState<JobStateOption>(
    DEFAULT_JOB_STATE_OPTION
  );

  const { contents, state: jobListState } = useRecoilValueLoadable(
    jobState.jobList({
      projectId,
      page,
      pageSize: DEFAULT_PAGE_SIZE,
      modality,
      states: stateOption.value === 'all' ? [] : [stateOption.value],
      annotatorId: userOption.value === 'all' ? undefined : userOption.value,
      type: jobTypeOption.value === 'all' ? undefined : jobTypeOption.value,
    })
  );

  const { jobs, countTotalPages, countTotalObjects } =
    jobListState === 'hasValue'
      ? contents
      : { jobs: [], countTotalPages: 1, countTotalObjects: 0 };

  const [annotatorOptions, setAnnotatorOptions] = useState<SelectOption[]>([
    DEFAULT_ANNOTATOR_OPTION,
  ]);
  const [isAnnotatorLoading, setIsAnnotatorLoading] = useState(true);

  // Load annotator options from the provided annotators
  useEffect(() => {
    let isMounted = true;
    setIsAnnotatorLoading(true);

    const loadOptions = async () => {
      // If no annotators provided, fetch from project
      const annotatorSchemas = annotators
        ? annotators
        : await import('src/api/projects')
            .then(({ getProject }) => getProject(projectId, modality))
            .then(project => project.annotators);

      // Fetch user info to get username/name for each annotator
      const { getUsersInfo } = await import('src/api/account');
      const allUsers = await getUsersInfo();
      const userMap = new Map(allUsers.map(user => [user.id, user]));

      if (isMounted) {
        const options: SelectOption[] = annotatorSchemas.map(annotator => {
          const userInfo = userMap.get(annotator.id);
          return {
            value: annotator.id,
            label: userInfo ? userInfo.username || userInfo.name : annotator.id,
          };
        });
        setAnnotatorOptions([DEFAULT_ANNOTATOR_OPTION, ...options]);
        setIsAnnotatorLoading(false);
      }
    };

    loadOptions().catch(() => {
      if (isMounted) {
        setAnnotatorOptions([DEFAULT_ANNOTATOR_OPTION]);
        setIsAnnotatorLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [annotators, projectId, modality]);

  const handleClickAddCases = () => {
    setIsDialogVisible(true);
  };

  const handleCloseAddCaseDialog = () => {
    setIsDialogVisible(false);
  };

  const handleUpdateAddCase = () => {
    setIsDialogVisible(false);
    refreshProject();
    refreshJobList();
    refreshProjectList();
  };

  const handleChangePage = (event: ChangeEvent<unknown>, page: number) =>
    setPage(page);

  const initPage = () => {
    setPage(1);
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getJobStateStyle = (
    state: JobState | '-'
  ): { backgroundColor: string; color: string } => {
    switch (state) {
      case 'created':
        return { backgroundColor: '#D0E7FF', color: '#0052CC' }; // Pastel blue
      case 'completed':
        return { backgroundColor: '#D4F4DD', color: '#1B7E38' }; // Pastel green
      case 'queued':
        return { backgroundColor: '#FFF7D0', color: '#8B6914' }; // Pastel yellow
      case 'assigned':
        return { backgroundColor: '#F4E4FF', color: '#6B2FB5' }; // Pastel purple
      case 'reported':
        return { backgroundColor: '#E8E8E8', color: '#424242' }; // Pastel gray
      default:
        return { backgroundColor: '#F5F5F5', color: '#666666' }; // Light gray
    }
  };

  const jobsColumnHeader = useMemo(() => {
    return [
      {
        Header: 'Job List',
        columns: [
          {
            Header: 'Job ID',
            accessor: 'id',
            style: { width: '40%' },
            Cell: ({ value }: { value: string }) => (
              <Flexbox style={{ alignItems: 'center', gap: '8px' }}>
                <span>{getEllipsis(value).string}</span>
                <Tooltip title="Copy Job ID">
                  <IconButton
                    size="small"
                    onClick={() => handleCopyToClipboard(value)}
                    style={{ padding: '4px' }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Flexbox>
            ),
          },
          {
            Header: 'Job Type',
            accessor: 'type',
            style: { width: '20%' },
          },
          {
            Header: 'Job State',
            id: 'jobState',
            style: { width: '20%' },
            accessor: ({
              reported,
              completed,
              createdAt,
              assignedAt,
              queuedAt,
            }: JobReadSchema): JobState | '-' =>
              reported
                ? 'reported'
                : completed
                ? 'completed'
                : !!queuedAt
                ? 'queued'
                : !!assignedAt
                ? 'assigned'
                : !!createdAt
                ? 'created'
                : '-',
            Cell: ({ value }: { value: JobState | '-' }) => {
              const style = getJobStateStyle(value);
              return (
                <Chip
                  label={value}
                  size="small"
                  style={{
                    backgroundColor: style.backgroundColor,
                    color: style.color,
                    fontWeight: 500,
                  }}
                />
              );
            },
          },
          {
            Header: 'Annotator',
            accessor: 'assignedTo',
            style: { width: '20%' },
            Cell: (cell: CellProps<JobReadSchema>) => {
              if (isAnnotatorLoading) return <Spinner size={26} />;
              const { row } = cell;
              const { assignedTo } = row.original;
              const user = annotatorOptions.find(
                opt => opt.value === assignedTo
              );
              return user?.label || 'None';
            },
          },
        ],
      },
    ];
  }, [isAnnotatorLoading, annotatorOptions]);

  return (
    <Vertical style={{ marginTop: '1rem' }}>
      {project.claim.type === 'Standard' ? (
        <AddCaseDialog
          open={isDialogVisible}
          projectId={projectId}
          onClose={handleCloseAddCaseDialog}
          onUpdate={handleUpdateAddCase}
          modality={modality}
        />
      ) : (
        <AddCPCCasesDialog
          open={isDialogVisible}
          projectId={projectId}
          onClose={handleCloseAddCaseDialog}
          onUpdate={handleUpdateAddCase}
          modality={modality}
        />
      )}

      <FilterPanel>
        <AnnotatorFilter options={annotatorOptions} onChange={initPage} />
        <JobTypeFilter
          state={jobTypeOption}
          setState={setJobTypeOption}
          onChange={initPage}
        />
        <JobStateFilter
          state={stateOption}
          setState={setStateOption}
          onChange={initPage}
        />
        <div style={{ marginLeft: 'auto', marginRight: '1rem' }}>
          <Tooltip
            title={
              project.confirmed
                ? 'Cannot add jobs to a confirmed project'
                : project.dataSource !== 'CASE_CURATOR'
                ? 'Jobs can only be added for CASE CURATOR data source'
                : ''
            }
          >
            <span>
              <Button
                color="primary"
                variant="contained"
                onClick={handleClickAddCases}
                disabled={
                  project.confirmed || project.dataSource !== 'CASE_CURATOR'
                }
                startIcon={<AddIcon />}
                style={{ color: 'white' }}
              >
                ADD JOB
              </Button>
            </span>
          </Tooltip>
        </div>
      </FilterPanel>

      <Typography variant="body2" style={{ marginTop: '20px', color: '#666' }}>
        Total Jobs: {countTotalObjects}
      </Typography>

      <TableWrapper>
        <Table<JobReadSchema>
          columns={jobsColumnHeader}
          data={jobs}
          isLoading={jobListState === 'loading'}
          isError={jobListState === 'hasError'}
          onRefresh={refreshJobList}
          paginationProps={{
            page,
            count: countTotalPages,
            onChange: handleChangePage,
          }}
          style={{ marginTop: 0 }}
        />
      </TableWrapper>
    </Vertical>
  );
}
